interface CoachingTopic {
    status?: string;
    description?: string;
    location?: string;
    priority?: string;
    [key: string]: unknown;
}

interface CoachingSession {
    welcomeMessage?: string;
    top3?: CoachingTopic[];
    [key: string]: unknown;
}

interface StartSessionOptions {
    autoInit?: boolean;
    trackName?: string;
    carName?: string;
    tts?: boolean;
    onWelcome?: (message: string, session: CoachingSession) => void;
}

interface EnsureMemoryOptions {
    autoInit?: boolean;
}

class CoachingMemory {
    apiBaseUrl: string;
    currentTrackId: string | null;
    currentCarId: string | null;
    currentMemory: unknown;
    sessionActive: boolean;

    constructor(options: { apiBaseUrl?: string } = {}) {
        this.apiBaseUrl = options.apiBaseUrl || '/api/coaching_memory';
        this.currentTrackId = null;
        this.currentCarId = null;
        this.currentMemory = null;
        this.sessionActive = false;
    }

    setContext(trackId: string, carId: string | null = null): void {
        this.currentTrackId = trackId;
        this.currentCarId = carId;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async request(action: string, method: string = 'GET', data: unknown = null): Promise<any> {
        const params = new URLSearchParams();
        params.append('action', action);

        if (this.currentTrackId) {
            params.append('track', this.currentTrackId);
        }
        if (this.currentCarId) {
            params.append('car', this.currentCarId);
        }

        const url = `${this.apiBaseUrl}?${params.toString()}`;

        const fetchOpts: RequestInit = {
            method: method,
            headers: {
                'Content-Type': 'application/json'
            }
        };

        if (data && (method === 'POST' || method === 'PUT')) {
            fetchOpts.body = JSON.stringify(data);
        }

        try {
            const response = await fetch(url, fetchOpts);
            const result = await response.json();

            if (!result.success) {
                throw new Error(result.error || 'Request failed');
            }

            return result;
        } catch (error) {
            console.error('CoachingMemory API error:', error);
            throw error;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getStatus(): Promise<any> {
        return await this.request('status');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getTop3(): Promise<any> {
        return await this.request('top3');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getTopics(filter: string = 'active'): Promise<any> {
        const result = await this.request('topics');
        if (filter !== 'all') {
            result.topics = result.topics.filter((t: CoachingTopic) =>
                filter === 'active' ? (t.status || 'active') === 'active' : true
            );
        }
        return result;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async startSession(options: StartSessionOptions = {}): Promise<any> {
        const result = await this.request('start', 'POST', {
            autoInit: options.autoInit !== false,
            trackName: options.trackName,
            carName: options.carName
        });

        this.sessionActive = true;
        this.currentMemory = result.session;

        if (options.onWelcome && result.session.welcomeMessage) {
            options.onWelcome(result.session.welcomeMessage, result.session);
        }

        if (options.tts !== false && result.session.welcomeMessage) {
            await this.announceWelcome(result.session.welcomeMessage);
        }

        return result;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async addTopic(issueType: string, description: string, priority: string = 'medium', context: Record<string, unknown> = {}): Promise<any> {
        return await this.request('add_topic', 'POST', {
            issueType,
            description,
            priority,
            context
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async updateTopic(topicId: string, updates: Record<string, unknown>): Promise<any> {
        return await this.request('update_topic', 'POST', {
            topicId,
            updates
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async markImproved(topicId: string, notes: string = ''): Promise<any> {
        return await this.request('improved', 'POST', {
            topicId,
            notes
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async addNote(note: string, category: string = 'general'): Promise<any> {
        return await this.request('note', 'POST', {
            note,
            category
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async initializeFromLaps(): Promise<any> {
        return await this.request('init_from_laps', 'POST');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async importAnalysis(analysisData: unknown): Promise<any> {
        return await this.request('import_analysis', 'POST', {
            analysis: analysisData
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async listTracks(): Promise<any> {
        const savedTrack = this.currentTrackId;
        this.currentTrackId = null;

        try {
            return await this.request('list_tracks');
        } finally {
            this.currentTrackId = savedTrack;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async clear(): Promise<any> {
        return await this.request('clear', 'POST');
    }

    async announceWelcome(message: string): Promise<void> {
        try {
            if (window.TTSAgentClient) {
                const tts = new window.TTSAgentClient();
                await tts.speak(message, 'normal');
                return;
            }

            if ('speechSynthesis' in window) {
                const utterance = new SpeechSynthesisUtterance(message);
                utterance.rate = 1.0;
                utterance.pitch = 1.0;
                window.speechSynthesis.speak(utterance);
            }
        } catch (error) {
            console.warn('TTS announcement failed:', error);
        }
    }

    formatTop3Summary(top3: CoachingTopic[]): string {
        if (!top3 || top3.length === 0) {
            return 'No active focus items. Everything is going well!';
        }

        const lines = top3.map((item, index) => {
            let line = `${index + 1}. ${item.description}`;
            if (item.location && item.location !== 'General') {
                line += ` (${item.location})`;
            }
            if (item.priority === 'high' || item.priority === 'critical') {
                line += ' [HIGH PRIORITY]';
            }
            return line;
        });

        return lines.join('\n');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async ensureMemory(options: EnsureMemoryOptions = {}): Promise<any> {
        const status = await this.getStatus();

        if (!status.hasMemory && options.autoInit !== false) {
            const initResult = await this.initializeFromLaps();

            if (initResult.topics && initResult.topics.length > 0) {
                return {
                    initialized: true,
                    fromLaps: true,
                    topics: initResult.topics,
                    top3: initResult.top3
                };
            }
        }

        return {
            initialized: status.hasMemory,
            fromLaps: false,
            topics: status.top3 || [],
            top3: status.top3 || []
        };
    }
}

interface CoachingMemoryIntegrationOptions {
    apiBaseUrl?: string;
    coachingTracker?: unknown;
    enabled?: boolean;
    autoAnnounce?: boolean;
    onWelcome?: ((message: string, session: CoachingSession) => void) | null;
    onTop3Update?: ((top3: CoachingTopic[]) => void) | null;
}

class CoachingMemoryIntegration {
    memory: CoachingMemory;
    coachingTracker: unknown;
    enabled: boolean;
    autoAnnounce: boolean;
    onWelcome: ((message: string, session: CoachingSession) => void) | null;
    onTop3Update: ((top3: CoachingTopic[]) => void) | null;

    constructor(options: CoachingMemoryIntegrationOptions = {}) {
        this.memory = new CoachingMemory(options);
        this.coachingTracker = options.coachingTracker || null;
        this.enabled = options.enabled !== false;
        this.autoAnnounce = options.autoAnnounce !== false;
        this.onWelcome = options.onWelcome || null;
        this.onTop3Update = options.onTop3Update || null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async initialize(trackId: string, carId: string, trackName: string, carName: string): Promise<any> {
        if (!this.enabled) return null;

        this.memory.setContext(trackId, carId);

        const result = await this.memory.startSession({
            trackName,
            carName,
            autoInit: true,
            tts: this.autoAnnounce,
            onWelcome: this.onWelcome ?? undefined
        });

        if (this.onTop3Update && result.session.top3) {
            this.onTop3Update(result.session.top3);
        }

        return result;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async recordIssue(issueType: string, description: string, priority: string = 'medium', context: Record<string, unknown> = {}): Promise<any> {
        if (!this.enabled) return null;
        return await this.memory.addTopic(issueType, description, priority, context);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async syncFromAnalysis(analysisData: unknown): Promise<any> {
        if (!this.enabled || !analysisData) return null;
        return await this.memory.importAnalysis(analysisData);
    }

    async getCurrentFocus(): Promise<CoachingTopic[]> {
        if (!this.enabled) return [];
        const result = await this.memory.getTop3();
        return result.top3 || [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async markFocusImproved(topicId: string, notes: string = ''): Promise<any> {
        if (!this.enabled) return null;

        const result = await this.memory.markImproved(topicId, notes);

        if (this.onTop3Update && result.top3) {
            this.onTop3Update(result.top3);
        }

        return result;
    }

    async endSession(): Promise<{ success: boolean }> {
        this.memory.sessionActive = false;
        return { success: true };
    }
}

window.CoachingMemory = CoachingMemory;
window.CoachingMemoryIntegration = CoachingMemoryIntegration;
